import { Controller } from "@hotwired/stimulus"

// Block 1 of the estimate editor (Informations générales): says what is still missing before its validation,
// fills the validity date from the 30/60/90 days shortcuts, and on a saved estimate folds the block into its
// summary. "Modifier" unfolds it; validating saves the estimate (erp--autosave) and folds it again. On a new
// estimate, validating submits the form, which creates the draft.
// Connects to data-controller="erp--estimate-general"
export default class extends Controller {
  static targets = ["fields", "summary", "edit", "missing", "validate", "title", "validity", "responsible"]

  static values = {
    persisted: Boolean,
    missingLabels: Object,
    missingPrefix: String,
    readyLabel: String
  }

  connect() {
    this.update()
  }

  update() {
    const missing = this.#missing()
    const ready = missing.length === 0

    if (this.titleTarget.value.trim()) this.titleTarget.removeAttribute("aria-invalid")
    this.validateTarget.setAttribute("aria-disabled", String(!ready))
    this.missingTarget.toggleAttribute("data-ready", ready)
    this.missingTarget.textContent = ready
      ? this.readyLabelValue
      : `${this.missingPrefixValue} ${missing.map(key => this.missingLabelsValue[key]).join(", ")}.`
  }

  validate(event) {
    const missing = this.#missing()
    if (missing.length > 0) {
      event.preventDefault()
      this.#focus(missing[0])
      return
    }
    if (!this.persistedValue) return

    event.preventDefault()
    this.#fold()
    this.element.closest("form")?.requestSubmit()
  }

  unfold() {
    this.fieldsTarget.parentElement.hidden = false
    if (this.hasSummaryTarget) this.summaryTarget.parentElement.hidden = true
    this.editTarget.hidden = true
    this.editTarget.setAttribute("aria-expanded", "true")
    this.titleTarget.focus()
  }

  applyValidity(event) {
    const date = new Date()
    date.setDate(date.getDate() + parseInt(event.target.value, 10))
    const pad = number => String(number).padStart(2, "0")
    this.validityTarget.value = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    this.validityTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  #missing() {
    const customer = this.element.querySelector("input[name='estimate[customer_id]']")
    return [
      !customer?.value && "customer",
      !this.titleTarget.value.trim() && "title",
      !this.validityTarget.value && "validity"
    ].filter(Boolean)
  }

  #focus(key) {
    if (key === "customer") {
      this.element.querySelector("[role='combobox']")?.focus()
    } else if (key === "title") {
      this.titleTarget.setAttribute("aria-invalid", "true")
      this.titleTarget.focus()
    } else {
      this.validityTarget.focus()
    }
  }

  // The summary shows what the fields hold now; the save then confirms it (erp--autosave#_syncGeneralSummary)
  #fold() {
    if (!this.hasSummaryTarget) return

    const validity = this.validityTarget.value
    this.#setField("title", this.titleTarget.value.trim())
    this.#setField("validity", validity ? new Intl.DateTimeFormat("fr-FR").format(new Date(`${validity}T00:00:00`)) : "")
    this.#setField("responsible", this.responsibleTarget.selectedOptions[0]?.textContent?.trim() || "")

    this.fieldsTarget.parentElement.hidden = true
    this.summaryTarget.parentElement.hidden = false
    this.editTarget.hidden = false
    this.editTarget.setAttribute("aria-expanded", "false")
    this.editTarget.focus()
  }

  #setField(name, value) {
    document.querySelectorAll(`[data-estimate-field='${name}']`).forEach(element => { element.textContent = value })
  }
}
